import { ProductItemsProp } from '@app/interfaces/order';

// Lưu các đơn hàng tạm (nhiều tab) vào localStorage, giữ đến khi user đóng hẳn tab đó.

export interface OrderDraftEntry {
  label: string;
  formValues: Record<string, unknown>;
  productItems: ProductItemsProp[];
  updatedAt: number;
  /** Khách chọn sẵn (vd "Tạo đơn nhanh" từ hộp thư) — bản ghi như search_for_order trả về. */
  customer?: Record<string, unknown> | null;
}

export type OrderDraftInit = Partial<Pick<OrderDraftEntry, 'label' | 'formValues' | 'customer'>>;

export interface OrderDraftStore {
  activeKey: string;
  order: string[];
  seq: number;
  drafts: Record<string, OrderDraftEntry>;
}

const STORAGE_KEY = 'order_draft_tabs';

const emptyStore = (): OrderDraftStore => ({ activeKey: '', order: [], seq: 1, drafts: {} });

const readStore = (): OrderDraftStore => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw) as OrderDraftStore;
    if (!parsed || typeof parsed !== 'object' || !parsed.drafts) return emptyStore();
    return parsed;
  } catch (error) {
    console.error('Đọc draft đơn hàng thất bại:', error);
    return emptyStore();
  }
};

const writeStore = (store: OrderDraftStore): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (error) {
    console.error('Lưu draft đơn hàng thất bại:', error);
  }
};

// Trả về store đã đảm bảo có ít nhất 1 tab.
export const getOrCreateStore = (): OrderDraftStore => {
  const store = readStore();
  if (store.order.length > 0 && store.drafts[store.activeKey]) return store;
  return createTab(store).store;
};

export const createTab = (base?: OrderDraftStore, init: OrderDraftInit = {}): { key: string; store: OrderDraftStore } => {
  const store = base ?? readStore();
  const key = String(store.seq);
  const entry: OrderDraftEntry = {
    label: init.label ?? `Đơn hàng ${key}`,
    formValues: init.formValues ?? {},
    productItems: [],
    updatedAt: Date.now(),
    customer: init.customer ?? null,
  };
  const next: OrderDraftStore = {
    ...store,
    seq: store.seq + 1,
    order: [...store.order, key],
    activeKey: key,
    drafts: { ...store.drafts, [key]: entry },
  };
  writeStore(next);
  return { key, store: next };
};

export const removeTab = (key: string): OrderDraftStore => {
  const store = readStore();
  const order = store.order.filter((k) => k !== key);
  const drafts = { ...store.drafts };
  delete drafts[key];

  let activeKey = store.activeKey;
  if (activeKey === key) {
    const idx = store.order.indexOf(key);
    activeKey = order[Math.max(0, idx - 1)] ?? order[0] ?? '';
  }

  const next: OrderDraftStore = { ...store, order, activeKey, drafts };
  writeStore(next);
  return next;
};

export const setActiveTab = (key: string): void => {
  const store = readStore();
  if (store.activeKey === key) return;
  writeStore({ ...store, activeKey: key });
};

export const getDraft = (key: string): OrderDraftEntry | undefined => readStore().drafts[key];

export const saveDraft = (
  key: string,
  patch: Partial<Pick<OrderDraftEntry, 'formValues' | 'productItems' | 'label' | 'customer'>>,
): void => {
  const store = readStore();
  const existing = store.drafts[key];
  if (!existing) return; // tab đã đóng — không hồi sinh
  const next: OrderDraftStore = {
    ...store,
    drafts: {
      ...store.drafts,
      [key]: { ...existing, ...patch, updatedAt: Date.now() },
    },
  };
  writeStore(next);
};

/** Tab chưa nhập gì (không giá trị form, không sản phẩm, không khách chọn sẵn). */
export const isPristineDraft = (entry: OrderDraftEntry | undefined): boolean =>
  !!entry && Object.keys(entry.formValues || {}).length === 0 && entry.productItems.length === 0 && !entry.customer;
